import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App as AntdApp, Descriptions, Space, Tag, Typography } from "antd";
import { Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import RestoreOptions, { DEFAULT_RESTORE_OPTIONS, buildIdsPayload } from "./RestoreOptions";
import RestoreResult from "./RestoreResult";
import { BACKUPS_ENDPOINT, RESTORE_MODE, formatBytes } from "../constants";

const { Text } = Typography;

/**
 * Restores one stored archive. The table list comes from the archive's manifest, so the
 * operator picks from what the file actually contains rather than from the live schema.
 */
const RestoreModal = ({ backupId, open, onClose, onDone }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effect — see the note in BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const { modal } = AntdApp.useApp();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [options, setOptions] = useState(DEFAULT_RESTORE_OPTIONS);
  const [result, setResult] = useState(null);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    await mutateRef.current("backupDetail", {
      method: "get",
      endpoint: `${BACKUPS_ENDPOINT}/${backupId}`,
      onSuccess: setDetail,
    });
    setLoading(false);
  }, [backupId]);

  useEffect(() => {
    if (!open || !backupId) return;
    setDetail(null);
    setResult(null);
    setOptions(DEFAULT_RESTORE_OPTIONS);
    loadDetail();
  }, [open, backupId, loadDetail]);

  const tables = useMemo(
    () =>
      (detail?.tables || []).map((t) => ({
        name: t.name,
        rowCount: t.rowCount,
        supportsIdFilter: t.supportsIdFilter,
        disabled: !t.existsInTarget,
        disabledReason: "This table no longer exists in the live database.",
      })),
    [detail]
  );

  const selectedNames = useMemo(
    () =>
      options.mode === RESTORE_MODE.FULL
        ? tables.filter((t) => !t.disabled).map((t) => t.name)
        : options.tables || [],
    [options.mode, options.tables, tables]
  );

  const runRestore = async () => {
    setRunning(true);
    setResult(null);

    await mutate("restoreBackup", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/${backupId}/restore`,
      payload: {
        mode: options.mode,
        strategy: options.strategy,
        tables: options.mode === RESTORE_MODE.SELECTIVE ? options.tables : undefined,
        ids: buildIdsPayload(options, selectedNames),
        skipSafetyBackup: options.skipSafetyBackup,
      },
      // The failure body carries the rolled-back result, which is worth showing too.
      onSuccess: (data) => {
        setResult(data);
        onDone?.();
      },
      onError: (body) => setResult(body?.data ?? null),
    });

    setRunning(false);
  };

  const confirmRestore = () => {
    if (options.mode === RESTORE_MODE.SELECTIVE && selectedNames.length === 0) {
      modal.warning({ title: "Nothing selected", content: "Pick at least one table to restore." });
      return;
    }

    modal.confirm({
      title: "Restore this backup?",
      centered: true,
      okText: "Restore",
      okType: "danger",
      content: (
        <span>
          {selectedNames.length} table{selectedNames.length === 1 ? "" : "s"} will be written into
          the live database. This cannot be undone except by restoring another backup.
        </span>
      ),
      onOk: runRestore,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={detail ? `Restore ${detail.fileName}` : "Restore backup"}
      size="xl"
      confirmText={result ? "Close" : "Restore"}
      cancelText="Cancel"
      danger={!result}
      loading={running}
      onConfirm={result ? onClose : confirmRestore}
    >
      {result ? (
        <RestoreResult result={result} />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {detail && (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Taken">
                {new Date(detail.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Size">{formatBytes(detail.sizeBytes)}</Descriptions.Item>
              <Descriptions.Item label="Source">
                <Text code>{detail.sourceServer || "—"}</Text> / {detail.sourceDatabase || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contents">
                <Space size={6}>
                  <Tag>{detail.tableCount} tables</Tag>
                  <Tag>{(detail.rowCount ?? 0).toLocaleString()} rows</Tag>
                </Space>
              </Descriptions.Item>
              {detail.note && (
                <Descriptions.Item label="Note" span={2}>
                  {detail.note}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          <RestoreOptions
            tables={tables}
            value={options}
            onChange={setOptions}
            loading={loading}
          />
        </Space>
      )}
    </Modal>
  );
};

export default RestoreModal;
